/**
 * Shared formatting utilities for consistent display across the application
 * Following DRY principle to avoid code duplication
 */

/**
 * Format a number as currency with proper locale
 */
export const formatCurrency = (value: number, currency = 'CHF'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Format a number as currency with decimals
 */
export const formatCurrencyWithDecimals = (value: number, currency = 'CHF', decimals = 2): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

/**
 * Format a number as percentage
 */
export const formatPercentage = (value: number, decimals = 1): string => {
  return `${value.toFixed(decimals)}%`;
};

/**
 * Format a number with thousand separators
 */
export const formatNumber = (value: number, decimals = 0): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

/**
 * Format large numbers with abbreviations (1.2M, 3.4B, etc.)
 */
export const formatLargeNumber = (value: number): string => {
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  
  if (absValue >= 1e9) {
    return `${sign}${(absValue / 1e9).toFixed(1)}B`;
  } else if (absValue >= 1e6) {
    return `${sign}${(absValue / 1e6).toFixed(1)}M`;
  } else if (absValue >= 1e3) {
    return `${sign}${(absValue / 1e3).toFixed(1)}K`;
  }
  return `${sign}${absValue.toFixed(0)}`;
};

/**
 * Format date to locale string
 * TASK-169: Now supports dynamic locale for i18n
 */
export const formatDate = (
  dateString: string | Date,
  locale = 'en-US',
  options?: Intl.DateTimeFormatOptions,
): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  return date.toLocaleDateString(locale, options ?? {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format date with time
 * TASK-169: Now supports dynamic locale for i18n
 */
export const formatDateTime = (dateString: string | Date, locale = 'en-US'): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format file size in human-readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Format an ISO timestamp as a relative, human-friendly string.
 * - Same day   → "Today HH:MM"
 * - Previous day → "Yesterday HH:MM"
 * - Older      → "MMM DD HH:MM"
 */
export const formatTimestamp = (iso: string): string => {
  const date = new Date(iso);
  const now = new Date();
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  if (date.toDateString() === now.toDateString()) return `Today ${time}`;

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return `Yesterday ${time}`;

  return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
};

/**
 * Format a raw value for display in label-value UI.
 * - null / undefined → '--'
 * - booleans         → 'Yes' / 'No'
 * - arrays           → comma-separated
 * - objects          → JSON string
 * - everything else  → String(value)
 */
export const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '--';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

/**
 * Return Tailwind CSS classes for a confidence score badge.
 * - > 0.85 → green
 * - >= 0.60 → yellow/amber
 * - < 0.60 → red
 */
export const getConfidenceColor = (confidence: number): string => {
  if (confidence > 0.85) return 'text-green-700 bg-green-50 border-green-200';
  if (confidence >= 0.6) return 'text-yellow-700 bg-yellow-50 border-yellow-200';
  return 'text-red-700 bg-red-50 border-red-200';
};